import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listTasks,
  getTaskById,
  createTask,
  updateTask,
  deleteTask,
  completeTask,
  skipTask,
  listTaskOccurrences,
} from '../services/taskService';
import type { CompleteTaskInput, SkipTaskInput, GroupedTasksResponse } from '../services/taskService';
import type {
  Task,
  CreateTaskInput,
  UpdateTaskInput,
  RecurrenceScope,
} from '../types/task';

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const taskKeys = {
  all: ['tasks'] as const,
  lists: () => [...taskKeys.all, 'list'] as const,
  list: (aquariumId: string) => [...taskKeys.lists(), aquariumId] as const,
  details: () => [...taskKeys.all, 'detail'] as const,
  detail: (taskId: string) => [...taskKeys.details(), taskId] as const,
  occurrences: (aquariumId: string) => [...taskKeys.all, 'occurrences', aquariumId] as const,
  occurrenceRange: (aquariumId: string, from: string, to: string) =>
    [...taskKeys.occurrences(aquariumId), from, to] as const,
};

/**
 * Lists, calendar ranges and other open details can all show the changed
 * occurrence (a recurring task appears on many dates), so every mutation
 * refreshes all task queries.
 */
export function invalidateAllTasks(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: taskKeys.all });
}

// ─── Grouped Task Shape ────────────────────────────────────────────────────────

export interface GroupedTasks {
  overdue: Task[];
  today: Task[];
  upcoming: Task[];
  completed: Task[];
}


// ─── useTaskList ──────────────────────────────────────────────────────────────

export function useTaskList(aquariumId: string) {
  const query = useQuery({
    queryKey: taskKeys.list(aquariumId),
    queryFn: async () => {
      const response = await listTasks(aquariumId);
      return response.data;
    },
    enabled: Boolean(aquariumId),
    staleTime: 30_000,
  });

  const raw: GroupedTasksResponse | undefined = query.data;
  const grouped: GroupedTasks = raw
    ? {
        overdue: raw.overdue ?? [],
        today: raw.today ?? [],
        upcoming: raw.upcoming ?? [],
        completed: raw.completed ?? [],
      }
    : { overdue: [], today: [], upcoming: [], completed: [] };

  const tasks: Task[] = raw
    ? [...(raw.overdue ?? []), ...(raw.today ?? []), ...(raw.upcoming ?? []), ...(raw.completed ?? [])]
    : [];

  return {
    tasks,
    grouped,
    isLoading: query.isPending && query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

// ─── useTaskOccurrences ───────────────────────────────────────────────────────

/** Calendar feed: every occurrence (recurring tasks expanded) in [from, to]. */
export function useTaskOccurrences(aquariumId: string, from: string, to: string) {
  const query = useQuery({
    queryKey: taskKeys.occurrenceRange(aquariumId, from, to),
    queryFn: async () => {
      const response = await listTaskOccurrences(aquariumId, from, to);
      return response.data;
    },
    enabled: Boolean(aquariumId && from && to),
    staleTime: 30_000,
    placeholderData: (previous: Task[] | undefined) => previous,
  });

  return {
    occurrences: query.data ?? [],
    isLoading: query.isPending && query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

// ─── useTaskDetail ────────────────────────────────────────────────────────────

export function useTaskDetail(taskId: string | null) {
  const query = useQuery({
    queryKey: taskId ? taskKeys.detail(taskId) : ['tasks', 'detail', 'none'],
    queryFn: async () => {
      if (!taskId) throw new Error('No taskId provided');
      const response = await getTaskById(taskId);
      return response.data;
    },
    enabled: Boolean(taskId),
    staleTime: 30_000,
  });

  return {
    task: query.data ?? null,
    isLoading: query.isPending && query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

// ─── useCreateTask ────────────────────────────────────────────────────────────

export function useCreateTask(aquariumId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateTaskInput) => createTask(aquariumId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.list(aquariumId) });
      invalidateAllTasks(queryClient);
    },
  });
}

// ─── useUpdateTask ────────────────────────────────────────────────────────────

export function useUpdateTask(aquariumId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, input, scope }: { taskId: string; input: UpdateTaskInput; scope?: RecurrenceScope }) =>
      updateTask(taskId, input, scope),
    onSuccess: (response, { taskId }) => {
      queryClient.setQueryData(taskKeys.detail(taskId), response.data);
      void queryClient.invalidateQueries({ queryKey: taskKeys.list(aquariumId) });
      invalidateAllTasks(queryClient);
    },
  });
}

// ─── useDeleteTask ────────────────────────────────────────────────────────────

export function useDeleteTask(aquariumId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    /** A task id, or `{ taskId, scope }` for one occurrence / the following ones / the series. */
    mutationFn: (target: string | { taskId: string; scope?: RecurrenceScope }) =>
      typeof target === 'string' ? deleteTask(target) : deleteTask(target.taskId, target.scope),
    onSuccess: (_response, target) => {
      const taskId = typeof target === 'string' ? target : target.taskId;
      queryClient.removeQueries({ queryKey: taskKeys.detail(taskId) });
      if (aquariumId) {
        void queryClient.invalidateQueries({ queryKey: taskKeys.list(aquariumId) });
      }
      invalidateAllTasks(queryClient);
    },
  });
}

// ─── useCompleteTask ──────────────────────────────────────────────────────────

export function useCompleteTask(aquariumId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, input }: { taskId: string; input?: CompleteTaskInput }) =>
      completeTask(taskId, input),
    onSuccess: (response, { taskId }) => {
      queryClient.setQueryData(taskKeys.detail(taskId), response.data);
      if (aquariumId) {
        void queryClient.invalidateQueries({ queryKey: taskKeys.list(aquariumId) });
      }
      invalidateAllTasks(queryClient);
      // Ticking off a dose of a manual dosing plan also logs it on the dosing page.
      if (response.data?.dosingScheduleId) {
        void queryClient.invalidateQueries({ queryKey: ['dosingLogs'] });
      }
    },
  });
}

// ─── useSkipTask ──────────────────────────────────────────────────────────────

export function useSkipTask(aquariumId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, input }: { taskId: string; input?: SkipTaskInput }) =>
      skipTask(taskId, input),
    onSuccess: (response, { taskId }) => {
      queryClient.setQueryData(taskKeys.detail(taskId), response.data);
      if (aquariumId) {
        void queryClient.invalidateQueries({ queryKey: taskKeys.list(aquariumId) });
      }
      invalidateAllTasks(queryClient);
    },
  });
}

// ─── useTasks (combined hook) ─────────────────────────────────────────────────

export function useTasks(aquariumId: string) {
  const listResult = useTaskList(aquariumId);
  const createMutation = useCreateTask(aquariumId);
  const updateMutation = useUpdateTask(aquariumId);
  const deleteMutation = useDeleteTask(aquariumId);
  const completeMutation = useCompleteTask(aquariumId);
  const skipMutation = useSkipTask(aquariumId);

  return {
    // List data
    tasks: listResult.tasks,
    grouped: listResult.grouped,
    isLoading: listResult.isLoading,
    isError: listResult.isError,
    error: listResult.error,
    refetch: listResult.refetch,

    // Mutations
    createTask: createMutation.mutateAsync,
    updateTask: updateMutation.mutateAsync,
    deleteTask: deleteMutation.mutateAsync,
    completeTask: completeMutation.mutateAsync,
    skipTask: skipMutation.mutateAsync,

    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isCompleting: completeMutation.isPending,
    isSkipping: skipMutation.isPending,

    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,
    completeError: completeMutation.error,
    skipError: skipMutation.error,
  };
}
